"use client"

import { memo, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import Logo from "@/components/Logo"

const DISMISS_KEY = "pv_fcm_prompt_dismissed"
const REGISTERED_KEY = "pv_fcm_registered"
const TOKEN_KEY = "pv_fcm_token"
const AWAITING_PERMISSION_KEY = "pv_fcm_awaiting_browser_permission"
/** Brave push fail: show error modal once per tab session (permission can still be granted) */
const BRAVE_PUSH_MODAL_SHOWN_KEY = "pv_fcm_brave_push_modal_shown"

// sessionStorage survives Chrome full navigations; it also survives refresh,
// so clear it once on a real reload (top-left refresh). Do not clear again on
// later in-app remounts of this same page load.
let reloadDismissConsumed = false
/** navigation.type stays "reload" for the whole tab — only clear awaiting once per real reload */
let reloadAwaitingConsumed = false
/** Survives Fast Refresh / MainLayout remount so Edge permission promise is not orphaned */
let sharedPermissionRequestResult = null
/** Unmount/HMR invalidates older runBrowserPermissionRequest chains (avoid stale setState/cleanup). */
let permissionFlowGeneration = 0

function consumeReloadDismiss() {
    if (reloadDismissConsumed) return
    reloadDismissConsumed = true
    try {
        const nav = performance.getEntriesByType("navigation")[0]
        const isReload = nav?.type === "reload" || performance.navigation?.type === 1
        if (isReload) window.sessionStorage.removeItem(DISMISS_KEY)
    } catch {
        // ignore
    }
}

function isDismissedThisVisit() {
    try {
        return window.sessionStorage.getItem(DISMISS_KEY) === "1"
    } catch {
        return false
    }
}

function markDismissedThisVisit() {
    try {
        window.sessionStorage.setItem(DISMISS_KEY, "1")
        window.localStorage.removeItem(DISMISS_KEY)
    } catch {
        // ignore
    }
}

function hasPublicFcmConfig() {
    return Boolean(
        process.env.NEXT_PUBLIC_FIREBASE_API_KEY &&
        process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID &&
        process.env.NEXT_PUBLIC_FIREBASE_APP_ID &&
        process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID &&
        process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY
    )
}

let fcmModulePromise = null

function loadFcmModule() {
    if (!fcmModulePromise) {
        fcmModulePromise = import("@/lib/firebaseClient")
    }
    return fcmModulePromise
}

function ensureFcmWarmup() {
    return loadFcmModule().then((mod) => mod.warmupFcm())
}

const PERMISSION_TIMEOUT_MS = 20000
const PERMISSION_TIMEOUT_MARKER = "__pv_fcm_permission_timeout__"
const PERMISSION_PENDING_CLASS = "pv-fcm-permission-pending"
/** Debug-only (FCM_LOG): Brave Privacy → Use Google services for push messaging */
const BRAVE_GOOGLE_PUSH_SETTING_HINT_EN =
    "Brave → Settings → Privacy → «Use Google services for push messaging» must be ON. Shields does not affect FCM token. "

const BRAVE_PUSH_USER_HINT_MR =
    "Brave → Settings → Privacy → «Use Google services for push messaging» चालू करा, पेज रिफ्रेश करा आणि पुन्हा प्रयत्न करा."

/** Chrome/Brave: site settings (tune) icon left of URL; Edge: lock */
function siteSettingsIconHintMr() {
    return "URL च्या डावीकडचे ⚙️ साइट सेटिंग चिन्ह"
}

/** Dev: always on. Deployed dev/prod: set NEXT_PUBLIC_FCM_DEBUG=1 at build. Filter Edge console by `[FCM]`. */
const FCM_LOG =
    process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_FCM_DEBUG === "1"

function fcmLog(event, detail) {
    if (!FCM_LOG) return
    if (detail !== undefined) {
        console.info("[FCM]", event, detail)
    } else {
        console.info("[FCM]", event)
    }
}

function fcmPermissionSnapshot(extra) {
    let navigationType = "unknown"
    try {
        navigationType = performance.getEntriesByType("navigation")[0]?.type ?? "unknown"
    } catch {
        // ignore
    }
    let awaitingSession = false
    try {
        awaitingSession = readAwaitingBrowserPermissionFlag()
    } catch {
        // ignore
    }
    return {
        ...(extra || {}),
        permission:
            typeof Notification !== "undefined" ? Notification.permission : "unsupported",
        dismissedVisit: isDismissedThisVisit(),
        awaitingSession,
        pendingUiClass:
            typeof document !== "undefined" &&
            document.documentElement.classList.contains(PERMISSION_PENDING_CLASS),
        path: typeof window !== "undefined" ? window.location.pathname : "",
        navigationType,
    }
}

function setPermissionPendingUi(active) {
    try {
        if (active) {
            document.documentElement.classList.add(PERMISSION_PENDING_CLASS)
        } else {
            document.documentElement.classList.remove(PERMISSION_PENDING_CLASS)
        }
    } catch {
        // ignore
    }
}

function isBraveBrowserSync() {
    if (typeof navigator === "undefined") return false
    if (navigator.brave) return true
    return /Brave/i.test(navigator.userAgent)
}

function isEdgeBrowserSync() {
    if (typeof navigator === "undefined") return false
    return /Edg\//.test(navigator.userAgent)
}

function isChromeBrowserSync() {
    if (typeof navigator === "undefined") return false
    const ua = navigator.userAgent
    if (isBraveBrowserSync() || isEdgeBrowserSync()) return false
    return /Chrome\//.test(ua) || /CriOS\//.test(ua)
}

function permissionBarClickHintMr() {
    if (isEdgeBrowserSync()) {
        return "🔒 लॉक चिन्ह (अॅड्रेस बार) → Site settings → Notifications → Allow किंवा 🔔 URL च्या उजवीकडील Notification चिन्ह → Allow"
    }
    if (isBraveBrowserSync() || isChromeBrowserSync()) {
        return `${siteSettingsIconHintMr()} → Notifications → Allow`
    }
    return `${siteSettingsIconHintMr()} किंवा 🔒 लॉक → Notifications → Allow`
}

function permissionTimeoutMessageMr() {
    return `सूचना परवानगीस वेळ लागला. ${permissionBarClickHintMr()}, नंतर पेज रिफ्रेश करा.`
}

function requestPermissionHintEn() {
    if (isBraveBrowserSync() || isChromeBrowserSync()) {
        return "If no Allow/Block: click the site settings (tune) icon left of the URL → Notifications → Allow."
    }
    if (isEdgeBrowserSync()) {
        return "If no Allow/Block: click the lock icon in the address bar → Notifications → Allow."
    }
    return "If no Allow/Block: site settings icon or lock in the address bar → Notifications → Allow."
}

function isLocalDevHost() {
    if (typeof window === "undefined") return false
    const host = window.location.hostname
    return host === "localhost" || host === "127.0.0.1"
}

function isPushServiceFailure(err) {
    const msg = String(err?.message || err || "")
    return /push service error|push service not available|messaging\/token-subscribe-failed/i.test(msg)
}

function isBravePushTokenFailure(err) {
    return isBraveBrowserSync() && isPushServiceFailure(err)
}

function shouldSuppressBravePushErrorModal() {
    if (isDismissedThisVisit()) return true
    try {
        return window.sessionStorage.getItem(BRAVE_PUSH_MODAL_SHOWN_KEY) === "1"
    } catch {
        return false
    }
}

function markBravePushErrorModalShown() {
    try {
        window.sessionStorage.setItem(BRAVE_PUSH_MODAL_SHOWN_KEY, "1")
    } catch {
        // ignore
    }
}

function clearBravePushErrorModalShown() {
    try {
        window.sessionStorage.removeItem(BRAVE_PUSH_MODAL_SHOWN_KEY)
    } catch {
        // ignore
    }
}

function logFcmPushServiceTroubleshooting(err) {
    if (!FCM_LOG) return
    const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "(unknown)"
    const host =
        typeof window !== "undefined" ? window.location.hostname : "(unknown)"
    const errName = err?.name ? String(err.name) : ""
    const errMsg = String(err?.message || err || "")
    console.warn("[FCM] push service failed:", errName || "(no name)", errMsg)
    const emptyNetworkNote =
        "Main-page Network often stays empty — getToken uses PushManager inside the service worker; look for «[FCM] getToken attempt … failed» above. " +
        "Optional: Application → Service Workers → firebase-messaging-sw.js → Inspect → Network. "
    if (isBraveBrowserSync() && isLocalDevHost()) {
        console.warn(
            "[FCM] tips: Permission is OK; Firebase Auth domains (incl. localhost) do not fix this. " +
                BRAVE_GOOGLE_PUSH_SETTING_HINT_EN +
                emptyNetworkNote +
                "Use Chrome or Edge on localhost for FCM dev. " +
                `Project ${projectId}, host «${host}».`
        )
        return
    }
    if (isBraveBrowserSync() && !isLocalDevHost()) {
        console.warn(
            "[FCM] tips: Notification permission is granted; Chrome/Edge on this URL prove deploy + VAPID + SW are OK. " +
                BRAVE_GOOGLE_PUSH_SETTING_HINT_EN +
                "Reset site 🔔 permission + unregister service worker + hard refresh. " +
                emptyNetworkNote +
                `Project ${projectId}, host «${host}».`
        )
        return
    }
    const braveNote = isBraveBrowserSync() ? BRAVE_GOOGLE_PUSH_SETTING_HINT_EN : ""
    console.warn(
        "[FCM] tips: " +
            braveNote +
            emptyNetworkNote +
            `Firebase ${projectId} → Authorized domains should include «${host}»; ` +
            "VAPID key in .env; Application → Service Workers → Unregister, hard refresh."
    )
}

function formatFcmEnableError(err) {
    const msg = String(err?.message || err || "")
    if (/Token register API failed|Unable to register notification token/i.test(msg)) {
        fcmLog("server register failed", err)
        return "सूचना सुरू करता आल्या नाहीत. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा."
    }
    if (/Service worker is not active/i.test(msg)) {
        return "सूचना सेटअप अपूर्ण आहे. पेज रिफ्रेश करून पुन्हा प्रयत्न करा."
    }
    if (isPushServiceFailure(err)) {
        logFcmPushServiceTroubleshooting(err)
        if (isBraveBrowserSync()) {
            return `सूचना परवानगी दिली आहे, पण नोंदणी पूर्ण झाली नाही. ${BRAVE_PUSH_USER_HINT_MR}`
        }
        if (isLocalDevHost()) {
            return "सूचना परवानगी दिली आहे, पण नोंदणी पूर्ण झाली नाही. Chrome किंवा Edge वापरून पुन्हा प्रयत्न करा."
        }
        return "सूचना परवानगी दिली आहे, पण नोंदणी पूर्ण झाली नाही. पेज रिफ्रेश करून पुन्हा प्रयत्न करा."
    }
    if (msg && !/FCM|firebase|token|push service|Service worker|AbortError|Registration failed/i.test(msg)) {
        return msg
    }
    return "सूचना सुरू करता आल्या नाहीत. कृपया पुन्हा प्रयत्न करा."
}

function releaseSharedPermissionRequest() {
    sharedPermissionRequestResult = null
}

/**
 * Call synchronously in the सुरु करा click handler — no sessionStorage/DOM/React before this.
 * Edge can hang the promise if the dialog is torn down via flushSync right after; legacy callback is a fallback.
 */
function beginBrowserNotificationPermissionRequest() {
    if (sharedPermissionRequestResult !== null) {
        fcmLog("requestPermission() reuse shared promise (remount/HMR)", fcmPermissionSnapshot())
        return sharedPermissionRequestResult
    }
    fcmLog("requestPermission() called", fcmPermissionSnapshot())

    const resultPromise = new Promise((resolve, reject) => {
        let settled = false
        const settle = (value) => {
            if (settled) return
            settled = true
            const perm = typeof value === "string" ? value : Notification.permission
            resolve(perm)
        }
        try {
            const returned = Notification.requestPermission((legacyResult) => {
                fcmLog("requestPermission legacy callback", { legacyResult })
                settle(legacyResult)
            })
            if (returned && typeof returned.then === "function") {
                returned
                    .then((value) => {
                        fcmLog("requestPermission promise settled", { value })
                        settle(value)
                    })
                    .catch(reject)
            } else if (typeof returned === "string") {
                fcmLog("requestPermission sync string", { value: returned })
                settle(returned)
            }
        } catch (err) {
            reject(err)
        }
    })

    sharedPermissionRequestResult = resultPromise
    fcmLog("requestPermission() returned", {
        type: "object",
        isPromise: true,
    })
    fcmLog("requestPermission hint", requestPermissionHintEn())
    return resultPromise
}

function readAwaitingBrowserPermissionFlag() {
    try {
        return window.sessionStorage.getItem(AWAITING_PERMISSION_KEY) === "1"
    } catch {
        return false
    }
}

/** Browser Allow/Block is pending; permission stays "default" until the user chooses. */
function isWaitingForBrowserPermissionChoice() {
    if (typeof Notification === "undefined") return false
    if (Notification.permission !== "default") return false
    return readAwaitingBrowserPermissionFlag()
}

function setAwaitingBrowserPermissionFlag(active) {
    try {
        if (active) {
            window.sessionStorage.setItem(AWAITING_PERMISSION_KEY, "1")
        } else {
            window.sessionStorage.removeItem(AWAITING_PERMISSION_KEY)
        }
    } catch {
        // ignore
    }
}

function resetAwaitingBrowserPermissionStorage() {
    setAwaitingBrowserPermissionFlag(false)
    setPermissionPendingUi(false)
}

/** User reset site notifications to Ask in browser settings — allow the marketing popup again. */
function clearDismissIfNotificationsPrompt() {
    try {
        window.sessionStorage.removeItem(DISMISS_KEY)
    } catch {
        // ignore
    }
}

/**
 * Waits for the result of Notification.requestPermission() (call requestPermission
 * synchronously in the click handler before awaiting this).
 */
function awaitNotificationPermissionResult(requestResult) {
    const fromNative = typeof requestResult?.then === "function"
        ? requestResult
        : Promise.resolve(requestResult)

    const fromPermissionStatus = new Promise((resolve) => {
        if (!navigator.permissions?.query) return
        navigator.permissions
            .query({ name: "notifications" })
            .then((status) => {
                const finish = () => resolve(Notification.permission)
                if (status.state === "granted" || status.state === "denied") {
                    finish()
                    return
                }
                const onChange = () => {
                    if (status.state === "prompt") return
                    status.removeEventListener("change", onChange)
                    finish()
                }
                status.addEventListener("change", onChange)
            })
            .catch(() => {})
    })

    const fromPoll = new Promise((resolve) => {
        const deadline = Date.now() + PERMISSION_TIMEOUT_MS
        const tick = () => {
            const perm = Notification.permission
            if (perm !== "default") {
                resolve(perm)
                return
            }
            if (Date.now() >= deadline) return
            window.setTimeout(tick, 150)
        }
        tick()
    })

    const timed = new Promise((resolve) => {
        window.setTimeout(() => resolve(PERMISSION_TIMEOUT_MARKER), PERMISSION_TIMEOUT_MS)
    })

    return Promise.race([fromNative, fromPermissionStatus, fromPoll, timed]).then((result) => {
        if (result === PERMISSION_TIMEOUT_MARKER) {
            fcmLog("permission wait finished", { outcome: "timeout", ...fcmPermissionSnapshot() })
            return PERMISSION_TIMEOUT_MARKER
        }
        const resolved = typeof result === "string" ? result : Notification.permission
        fcmLog("permission wait finished", { outcome: resolved, raw: result, ...fcmPermissionSnapshot() })
        return resolved
    })
}

async function registerToken() {
    fcmLog("registerToken start", fcmPermissionSnapshot())
    const { requestFcmToken } = await loadFcmModule()
    const token = await requestFcmToken()
    if (!token) throw new Error("FCM token was not created")
    let previousToken = ""
    try {
        previousToken = window.localStorage.getItem(TOKEN_KEY) || ""
    } catch {
        previousToken = ""
    }
    const response = await fetch("/api/fcm/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            token,
            previousToken: previousToken && previousToken !== token ? previousToken : undefined,
        }),
    })
    if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        fcmLog("register API failed", { status: response.status, payload })
        throw new Error(payload?.error || "Token register API failed")
    }
    try {
        window.localStorage.setItem(REGISTERED_KEY, "1")
        window.localStorage.setItem(TOKEN_KEY, token)
    } catch {
        // ignore storage errors
    }
    clearBravePushErrorModalShown()
    fcmLog("registerToken ok", { tokenLen: token?.length ?? 0 })
    return true
}

function FcmPromptLogo() {
    return (
        <div className="inline-flex items-center bg-[#1C3C96] rounded-[2px] px-[0.5em] py-[0.16em] [&_.logo-brand]:text-[1.08rem] sm:[&_.logo-brand]:text-[1.1rem]">
            <Logo inverted />
        </div>
    )
}

function BellIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden="true" className="shrink-0">
            <path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
    )
}

function FcmSettingUpLoader() {
    return (
        <div className="flex flex-col items-center justify-center px-6 py-12 sm:py-14 text-center">
            <div
                className="h-11 w-11 rounded-full border-[3px] border-[#E5E7EB] border-t-[#B71C1C] animate-spin"
                aria-hidden="true"
            />
            <p id="fcm-prompt-title" className="mt-5 text-[20px] sm:text-[22px] font-bold text-[#0A1E3F] leading-snug">
                नोटिफिकेशन्स सेट होत आहेत...
            </p>
            <p className="mt-2 text-[15px] sm:text-[16px] text-[#4B5563] leading-relaxed max-w-[320px]">
                कृपया थोडा वेळ थांबा. ताज्या बातम्यांच्या सूचना लवकरच सुरु होतील.
            </p>
        </div>
    )
}

const FCM_CATEGORIES = ["POLITICS", "BUSINESS", "SPORTS", "NATION", "& MORE"]

function FcmPromptHero() {
    return (
        <div className="relative w-[min(100%,280px)] aspect-square">
            <img
                src="/images/fcm-notify-circle.jpg"
                alt=""
                width={280}
                height={280}
                className="w-full h-full object-contain select-none pointer-events-none"
            />
            <div className="absolute bottom-[15%] right-[-2%] min-w-[72px] bg-white rounded-l-[11px] py-[10px] pl-[12px] pr-[9px] shadow-[0_5px_16px_rgba(15,23,42,0.12)]">
                <ul className="space-y-[6px]">
                    {FCM_CATEGORIES.map((category) => (
                        <li
                            key={category}
                            className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-[#3D4654] leading-none whitespace-nowrap"
                        >
                            {category}
                        </li>
                    ))}
                </ul>
                <span className="mt-[9px] block w-[20px] h-[2px] rounded-full bg-[#C62828]" aria-hidden="true" />
            </div>
        </div>
    )
}

const FcmSubscriber = ({ silent = false }) => {
    const [visible, setVisible] = useState(false)
    const [blocked, setBlocked] = useState(false)
    const [error, setError] = useState("")
    const [busy, setBusy] = useState(false)
    const [settingUp, setSettingUp] = useState(false)
    const enableInFlightRef = useRef(false)
    /** While prompt is open permission stays "default"; do not reopen the overlay. */
    const awaitingBrowserPermissionRef = useRef(false)

    useEffect(() => {
        try {
            const nav = performance.getEntriesByType("navigation")[0]
            const isReload = nav?.type === "reload" || performance.navigation?.type === 1
            if (isReload && !reloadAwaitingConsumed) {
                reloadAwaitingConsumed = true
                fcmLog("mount: first load after reload — clear stale awaiting flag", fcmPermissionSnapshot())
                resetAwaitingBrowserPermissionStorage()
                releaseSharedPermissionRequest()
            } else {
                fcmLog("mount: remount — keep awaiting / shared promise", {
                    ...fcmPermissionSnapshot(),
                    hasSharedPermissionPromise: sharedPermissionRequestResult !== null,
                })
                if (!isWaitingForBrowserPermissionChoice()) {
                    setPermissionPendingUi(false)
                }
            }
        } catch {
            setPermissionPendingUi(false)
        }
        return () => {
            if (isWaitingForBrowserPermissionChoice() || sharedPermissionRequestResult !== null) {
                permissionFlowGeneration += 1
                fcmLog("unmount during browser permission wait — invalidate flow, will resume on remount", {
                    permissionFlowGeneration,
                    ...fcmPermissionSnapshot(),
                })
            }
            setPermissionPendingUi(false)
        }
    }, [])

    useEffect(() => {
        if (!hasPublicFcmConfig()) return
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            ensureFcmWarmup().catch(() => {})
        }
    }, [])

    useEffect(() => {
        if (!hasPublicFcmConfig()) {
            fcmLog("boot skipped — missing NEXT_PUBLIC_FIREBASE_* env at build")
            return
        }
        if (typeof Notification === "undefined") {
            if (!silent) {
                setError("This browser does not support notifications.")
                setVisible(true)
            }
            return
        }

        let cancelled = false
        let permissionStatus = null
        let onPermissionChange = null

        const suppressAskPrompt = () => {
            if (enableInFlightRef.current) return "enableInFlight"
            if (awaitingBrowserPermissionRef.current) return "awaitingRef"
            if (isWaitingForBrowserPermissionChoice()) return "awaitingSession"
            return null
        }

        const boot = async () => {
            fcmLog("boot", { silent, ...fcmPermissionSnapshot() })
            if (Notification.permission === "granted") {
                try {
                    await registerToken()
                } catch (err) {
                    fcmLog("silent register failed", err)
                    if (!cancelled && !silent) {
                        if (isBravePushTokenFailure(err) && shouldSuppressBravePushErrorModal()) {
                            fcmLog("boot: Brave push fail — modal suppressed this visit", fcmPermissionSnapshot())
                            return
                        }
                        if (isBravePushTokenFailure(err)) {
                            markBravePushErrorModalShown()
                        }
                        setError(
                            formatFcmEnableError(err) ||
                                "सूचना परवानगी दिली आहे, पण नोंदणी झाली नाही. पुन्हा प्रयत्न करा."
                        )
                        setVisible(true)
                    }
                }
                return
            }

            if (silent) return

            if (Notification.permission === "denied") {
                fcmLog("boot: permission denied — hide popup")
                if (!cancelled) setVisible(false)
                return
            }

            // permission is "default" (Ask)
            consumeReloadDismiss()
            try {
                window.localStorage.removeItem(DISMISS_KEY)
            } catch {
                // ignore
            }

            if (isDismissedThisVisit()) {
                fcmLog("boot: popup suppressed — नंतर dismissed this visit")
                return
            }
            const suppressReason = suppressAskPrompt()
            if (suppressReason) {
                fcmLog("boot: popup suppressed", { reason: suppressReason, ...fcmPermissionSnapshot() })
                return
            }
            if (!cancelled) {
                fcmLog("boot: show marketing popup")
                setBlocked(false)
                setVisible(true)
            }
        }

        const syncAskPrompt = (source = "unknown") => {
            if (cancelled || silent) return
            if (typeof Notification === "undefined") return
            if (isDismissedThisVisit()) {
                fcmLog("syncAskPrompt: skip dismissed", { source })
                return
            }
            if (Notification.permission === "denied") {
                setVisible(false)
                return
            }
            const suppressReason = suppressAskPrompt()
            if (suppressReason) {
                fcmLog("syncAskPrompt: suppressed", { source, reason: suppressReason })
                return
            }
            if (Notification.permission !== "default") return
            try {
                window.localStorage.removeItem(DISMISS_KEY)
            } catch {
                // ignore
            }
            fcmLog("syncAskPrompt: show marketing popup", { source })
            setBlocked(false)
            setVisible(true)
        }

        const onVisible = () => {
            if (document.visibilityState === "visible") syncAskPrompt("visibility")
        }

        const onFocus = () => syncAskPrompt("focus")

        boot()
        window.addEventListener("focus", onFocus)
        document.addEventListener("visibilitychange", onVisible)
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: "notifications" }).then((status) => {
                if (cancelled) return
                permissionStatus = status
                fcmLog("Permissions API notifications", { state: status.state })
                let lastPermissionState = status.state
                onPermissionChange = () => {
                    fcmLog("Permissions API change", {
                        from: lastPermissionState,
                        to: status.state,
                    })
                    // Only when user resets Block → Ask in browser settings (not on every page visit)
                    if (lastPermissionState === "denied" && status.state === "prompt") {
                        clearDismissIfNotificationsPrompt()
                    }
                    lastPermissionState = status.state
                    syncAskPrompt("permissions-change")
                }
                status.addEventListener("change", onPermissionChange)
                syncAskPrompt("permissions-query")
            }).catch((err) => {
                fcmLog("Permissions API query failed", err)
            })
        }
        return () => {
            cancelled = true
            fcmLog("FcmSubscriber unmount (route change remounts MainLayout)")
            window.removeEventListener("focus", onFocus)
            document.removeEventListener("visibilitychange", onVisible)
            if (permissionStatus && onPermissionChange) {
                permissionStatus.removeEventListener("change", onPermissionChange)
            }
        }
    }, [silent])

    const dismiss = () => {
        if (busy || settingUp) return
        markDismissedThisVisit()
        fcmLog("user clicked नंतर — hide until reload", fcmPermissionSnapshot())
        setVisible(false)
    }

    const completeRegistration = async () => {
        flushSync(() => {
            setVisible(true)
            setSettingUp(true)
            setError("")
        })
        await ensureFcmWarmup()
        await registerToken()
        setVisible(false)
    }

    const clearAwaitingBrowserPermission = () => {
        fcmLog("clear awaiting browser permission", fcmPermissionSnapshot())
        awaitingBrowserPermissionRef.current = false
        setAwaitingBrowserPermissionFlag(false)
        setPermissionPendingUi(false)
    }

    const finishPermissionFlow = async (permission) => {
        fcmLog("finishPermissionFlow", { permission })
        if (permission === PERMISSION_TIMEOUT_MARKER) {
            flushSync(() => {
                setVisible(true)
                setError(permissionTimeoutMessageMr())
            })
            return
        }
        if (permission === "denied") {
            return
        }
        if (permission !== "granted") {
            flushSync(() => {
                setVisible(true)
                setError(
                    `ब्राउझरने सूचना परवानगी विचारली नाही. ${permissionBarClickHintMr()}, नंतर पुन्हा प्रयत्न करा.`
                )
            })
            return
        }
        await completeRegistration()
    }

    const runBrowserPermissionRequest = (requestResult) => {
        const flowGen = ++permissionFlowGeneration
        fcmLog("permission wait attach", { flowGen, ...fcmPermissionSnapshot() })
        void awaitNotificationPermissionResult(requestResult)
            .then(async (permission) => {
                if (flowGen !== permissionFlowGeneration) {
                    fcmLog("permission wait result ignored (stale flow)", { flowGen, permissionFlowGeneration })
                    return
                }
                clearAwaitingBrowserPermission()
                await finishPermissionFlow(permission)
            })
            .catch((err) => {
                if (flowGen !== permissionFlowGeneration) return
                clearAwaitingBrowserPermission()
                fcmLog("permission flow error", err)
                if (isBravePushTokenFailure(err)) markBravePushErrorModalShown()
                flushSync(() => {
                    setVisible(true)
                    setError(formatFcmEnableError(err))
                })
            })
            .finally(() => {
                if (flowGen !== permissionFlowGeneration) {
                    fcmLog("permission wait finally skipped (stale flow)", { flowGen, permissionFlowGeneration })
                    return
                }
                releaseSharedPermissionRequest()
                clearAwaitingBrowserPermission()
                enableInFlightRef.current = false
                setBusy(false)
                setSettingUp(false)
            })
    }

    useEffect(() => {
        if (!hasPublicFcmConfig()) return
        if (typeof Notification === "undefined") return

        const awaiting = isWaitingForBrowserPermissionChoice()
        const hasShared = sharedPermissionRequestResult !== null
        if (!awaiting && !hasShared) return

        const permNow = Notification.permission
        if (permNow !== "default") {
            fcmLog("resume: permission decided while component was unmounted", { permNow })
            enableInFlightRef.current = true
            awaitingBrowserPermissionRef.current = true
            void (async () => {
                try {
                    clearAwaitingBrowserPermission()
                    await finishPermissionFlow(permNow)
                } finally {
                    releaseSharedPermissionRequest()
                    enableInFlightRef.current = false
                }
            })()
            return
        }

        if (enableInFlightRef.current) return

        fcmLog("resume: re-attach permission wait (Fast Refresh or SPA remount)", {
            awaiting,
            hasShared,
            ...fcmPermissionSnapshot(),
        })
        enableInFlightRef.current = true
        awaitingBrowserPermissionRef.current = true
        setPermissionPendingUi(true)
        const requestResult =
            hasShared && sharedPermissionRequestResult !== null
                ? sharedPermissionRequestResult
                : new Promise(() => {})
        runBrowserPermissionRequest(requestResult)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resume once on mount only
    }, [])

    const enable = () => {
        if (blocked || busy || settingUp || enableInFlightRef.current) {
            fcmLog("enable ignored", { blocked, busy, settingUp, enableInFlight: enableInFlightRef.current })
            return
        }
        if (typeof Notification === "undefined") return

        const permissionNow = Notification.permission
        fcmLog("enable clicked — नोटिफिकेशन्स सुरु करा", { permissionNow, ...fcmPermissionSnapshot() })

        if (permissionNow === "granted") {
            enableInFlightRef.current = true
            setError("")
            setBusy(true)
            void completeRegistration()
                .catch((err) => {
                    fcmLog("enable failed", err)
                    if (isBravePushTokenFailure(err)) markBravePushErrorModalShown()
                    flushSync(() => {
                        setVisible(true)
                        setError(formatFcmEnableError(err))
                    })
                })
                .finally(() => {
                    enableInFlightRef.current = false
                    setBusy(false)
                    setSettingUp(false)
                })
            return
        }

        if (permissionNow === "denied") {
            fcmLog("enable: already denied in browser")
            setVisible(false)
            return
        }

        // User gesture: requestPermission must run before sessionStorage, DOM classList, or React updates.
        const requestResult = beginBrowserNotificationPermissionRequest()

        enableInFlightRef.current = true
        awaitingBrowserPermissionRef.current = true
        setAwaitingBrowserPermissionFlag(true)
        setPermissionPendingUi(true)
        setError("")
        setBusy(false)
        // Hide via CSS only — do not flushSync/unmount the dialog; Edge can fail to show the native prompt if the overlay is removed synchronously after requestPermission().
        runBrowserPermissionRequest(requestResult)
    }

    if (!visible && !settingUp) return null

    const notificationsAlreadyAllowed =
        typeof Notification !== "undefined" && Notification.permission === "granted"

    return (
        <div
            data-fcm-overlay
            className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 bg-[rgba(12,18,38,0.42)] backdrop-blur-[5px]"
        >
            <div
                role="dialog"
                aria-labelledby="fcm-prompt-title"
                aria-busy={settingUp}
                className="relative w-full max-w-[620px] rounded-[18px] bg-white shadow-[0_22px_60px_rgba(15,23,42,0.26)]"
            >
                {!settingUp && (
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="बंद करा"
                        disabled={busy}
                        className="absolute top-[18px] right-[18px] z-20 w-7 h-7 flex items-center justify-center text-[#6B7280] hover:text-[#1A1A1A] transition-colors cursor-pointer bg-transparent border-0 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                )}

                {settingUp ? (
                    <FcmSettingUpLoader />
                ) : (
                <>
                <div className="flex flex-col sm:flex-row sm:items-start sm:gap-0">
                    <div className={`flex-1 min-w-0 px-5 pt-5 sm:pl-6 sm:pt-6 sm:pr-2 ${blocked ? "pb-5 sm:pb-6" : "pb-0"}`}>
                        <FcmPromptLogo />

                        {!blocked && (
                            <div className="mt-[18px]">
                                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#0A2456] leading-[1.5]">
                                    REAL NEWS
                                    <br />
                                    REAL IMPACT
                                </p>
                                <span className="mt-[6px] mb-[18px] block w-[36px] h-[2.5px] rounded-full bg-[#C62828]" />
                            </div>
                        )}

                        <h2
                            id="fcm-prompt-title"
                            className="text-[32px] sm:text-[40px] leading-[1.14] font-bold tracking-[-0.015em]"
                        >
                            {blocked ? (
                                <span className="text-[#0A1E3F]">सूचना बंद आहेत</span>
                            ) : (
                                <>
                                    <span className="block text-[#0A1E3F]">ताज्या बातम्यांची</span>
                                    <span className="block text-[#B71C1C]">सूचना मिळवा</span>
                                </>
                            )}
                        </h2>

                        <p className="mt-3 text-[18px] sm:text-[20px] leading-[1.55] font-normal text-[#111827] max-w-[320px]">
                            {blocked ? (
                                `${permissionBarClickHintMr()}, नंतर पेज रिफ्रेश करा.`
                            ) : (
                                <>
                                    महत्वाच्या आणि ताज्या बातम्यांचे
                                    <br />
                                    अपडेट्स त्वरित मिळवा.
                                </>
                            )}
                        </p>

                        {error && (
                            <p className="text-xs text-red-600 mt-3">{error}</p>
                        )}

                        {blocked && (
                            <button
                                type="button"
                                onClick={dismiss}
                                className="mt-6 block text-[14px] font-normal text-[#1C3C96] underline underline-offset-[3px] decoration-[#1C3C96]/60 hover:decoration-[#1C3C96] hover:text-[#163381] cursor-pointer bg-transparent border-0 transition-colors"
                            >
                                नंतर
                            </button>
                        )}
                    </div>

                    {!blocked && (
                        <div className="flex shrink-0 w-full sm:w-auto max-w-[240px] sm:max-w-[290px] mx-auto sm:mx-0 items-start justify-center sm:justify-start sm:-ml-5 sm:-translate-x-[0.5cm] px-5 sm:px-0 pt-4 sm:pt-2 pb-2 sm:pb-4 overflow-visible">
                            <FcmPromptHero />
                        </div>
                    )}
                </div>

                {!blocked && (
                    <div className="px-5 pb-5 pt-6 sm:px-6 sm:pb-6 w-full">
                        <button
                            type="button"
                            onClick={enable}
                            disabled={busy}
                            className="flex w-full h-[64px] items-center justify-center gap-[16px] rounded-[12px] bg-gradient-to-r from-[#EB3A31] to-[#7F1212] hover:from-[#D93027] hover:to-[#6E0F0F] text-white text-[18px] sm:text-[20px] font-bold disabled:opacity-70 cursor-pointer shadow-[0_6px_16px_rgba(127,18,18,0.38)] transition-all hover:shadow-[0_8px_20px_rgba(127,18,18,0.45)] px-5 border-0"
                        >
                            <BellIcon />
                            <span className="h-[28px] w-[1.5px] bg-white/80 shrink-0" aria-hidden="true" />
                            <span className="tracking-[0.01em] leading-none">
                                {busy
                                    ? "प्रतीक्षा करा..."
                                    : notificationsAlreadyAllowed && error
                                      ? "पुन्हा प्रयत्न करा"
                                      : "नोटिफिकेशन्स सुरु करा"}
                            </span>
                            <svg width="11" height="18" viewBox="0 0 8 14" fill="none" aria-hidden="true" className="shrink-0">
                                <path d="M1 1.2L6.6 7 1 12.8" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={dismiss}
                            disabled={busy}
                            className="mt-[18px] mx-auto block text-[14px] font-normal text-[#1C3C96] underline underline-offset-[3px] decoration-[#1C3C96]/60 hover:decoration-[#1C3C96] hover:text-[#163381] cursor-pointer bg-transparent border-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            नंतर
                        </button>
                    </div>
                )}
                </>
                )}
            </div>
        </div>
    )
}

export default memo(FcmSubscriber)
